import { useEffect, useState } from 'react';
import { useFund } from '../context/FundContext';
import FundDateBar from '../components/FundDateBar';
import { Loading } from '../components/Loader';
import { api } from '../api';
import { shortName } from '../format';

// 2-decimal number, negatives shown in parentheses (accounting style).
function fmt2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${s})` : s;
}

const TABS = [
  { key: 'realized', label: 'Realized G/L' },
  { key: 'unrealized', label: 'Unrealized G/L' },
];

export default function GainLoss() {
  const { fund, date } = useFund();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('realized');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!fund || !date) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    api.gainLoss(fund, date)
      .then((data) => { if (active) { setD(data); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); setD(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fund, date]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await api.gainLossExport(fund, date, tab);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const rows = d ? d[tab] : [];
  const isReal = tab === 'realized';
  const p = isReal ? 'realized' : 'unrealized';

  return (
    <div>
      <FundDateBar />
      {error && <div className="error">Error: {error}</div>}
      {loading && <Loading label="Loading gain / loss…" />}

      {d && !loading && (
        <>
          <div className="gl-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`gl-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
            <div className="gl-tab-spacer" />
            <button className="btn" onClick={exportXlsx} disabled={exporting || !rows.length}>
              {exporting ? 'Exporting…' : 'Export XLSX'}
            </button>
          </div>

          <div className="notice">
            {isReal ? 'Realized' : 'Unrealized'} gain / loss as of {d.snapshotDate || date}
            {' · '}{rows.length} row{rows.length === 1 ? '' : 's'}
            {' · '}Total (BC): {fmt2(isReal ? d.totals.realizedBC : d.totals.unrealizedBC)}
          </div>

          <div className="panel gl-panel">
            {rows.length ? (
              <div className="gl-table-wrap">
                <table className="table gl-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Symbol</th><th>Security</th><th>CCY</th>
                      <th>Asset Class</th><th>Fund</th><th>Account</th>
                      <th className="num">Quantity</th>
                      <th className="num">{isReal ? 'Realized' : 'Unrealized'} G/L (LC)</th>
                      <th className="num">{isReal ? 'Realized' : 'Unrealized'} G/L Price (BC)</th>
                      <th className="num">{isReal ? 'Realized' : 'Unrealized'} G/L FX (BC)</th>
                      <th className="num">{isReal ? 'Realized' : 'Unrealized'} G/L (BC)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.symbol}-${r.account}-${i}`}>
                        <td>{r.date}</td>
                        <td>{r.symbol}</td>
                        <td>{shortName(r.security)}</td>
                        <td>{r.ccy}</td>
                        <td>{r.assetClass}</td>
                        <td>{shortName(r.fund)}</td>
                        <td>{r.account}</td>
                        <td className="num">{fmt2(r.quantity)}</td>
                        <td className={`num ${r[`${p}LC`] < 0 ? 'loss' : ''}`}>{fmt2(r[`${p}LC`])}</td>
                        <td className={`num ${r[`${p}PriceBC`] < 0 ? 'loss' : ''}`}>{fmt2(r[`${p}PriceBC`])}</td>
                        <td className={`num ${r[`${p}FxBC`] < 0 ? 'loss' : ''}`}>{fmt2(r[`${p}FxBC`])}</td>
                        <td className={`num ${r[`${p}BC`] < 0 ? 'loss' : ''}`}>{fmt2(r[`${p}BC`])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">
                No {isReal ? 'realized' : 'unrealized'} gain / loss for this fund on {d.snapshotDate || date}.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
